import { getCurrentUser, getDfBHost } from "../../Util/helper";

/**
 * Creates a user card component and appends it to the provided wrapper
 * @param {HTMLElement} wrapper - The container element to append the component to
 * @param {Object} user - User object with properties: id, username, image, displayname, types, friends, createdAt
 * @param {Object} config - Configuration object (optional)
 * @param {string} config.position - Position of hover card: 'auto'|'right'|'left'|'top'|'bottom'|'bottom-left'
 * @param {Object} config.show - What to show in the main card
 * @param {boolean} config.show.avatar - Whether to show avatar (default: true)
 * @param {boolean} config.show.name - Whether to show username (default: true)
 * @param {boolean} config.show.hover - Whether to show hover card (default: true)
 * @param {boolean} config.show.currentDate - Whether to show current date inline with displayname
 * @param {string} config.show.visibleSlotHTML - HTML content to show in visible area
 * @param {string} config.hoverSlotHTML - Additional HTML for hover area
 * @param {string} config.extraHoverHTML - Extra HTML content for hover card
 * @param {string} config.size - CSS class for avatar size (default: 'size-14')
 * @param {string} config.date - Custom date to show inline with displayname (ISO string or Date)
 * @param {string} config.subtext - Subtext to show below username (shifts layout upward)
 * @returns {HTMLElement} The created user card element
 */
export function createUserCard(wrapper, user, config = {}) {
    const cfg = (config && typeof config === 'object') ? config : {};
    const show = (cfg.show && typeof cfg.show === 'object') ? cfg.show : {};
    const pos = cfg.position || 'bottom';
    const showAvatar = show.avatar !== false;
    const showName = show.name !== false;
    const showExpanded = show.hover !== false || show.expanded === true;
    const showCurrentDate = show.currentDate === true;
    const visibleSlotHTML = show.visibleSlotHTML || '';
    const hoverSlotHTML = cfg.hoverSlotHTML || '';
    const size = cfg.size || 'size-10';
    const headText = cfg.headText || '';
    const subtext = cfg.subtext || '';
    const localDate = cfg.date || null;
    const localUser = getCurrentUser();
    const topDomain = getDfBHost()
    
    const container = document.createElement('div');
    container.className = 'relative inline-block group cursor-pointer';
    
    const visibleContent = document.createElement('div');
    visibleContent.className = 'flex gap-2';
    
    if (headText) {
        const headSpan = document.createElement('span');
        headSpan.className = 'text-sm text-base-content/70 mr-2 flex items-center cursor-default';
        headSpan.textContent = headText;
        visibleContent.appendChild(headSpan);
    }

    if (showAvatar) {
        const avatarLink = document.createElement('a');
        avatarLink.className = 'avatar flex-shrink-0';
        
        const avatarContainer = document.createElement('div');
        avatarContainer.className = `rounded-full ${size} bg-base-200 overflow-hidden flex items-center justify-center`;

        const avatarImg = document.createElement('img');
        avatarImg.src = user.image;
        avatarImg.className = 'w-full h-full object-contain rounded-full bg-inherit';
        avatarImg.alt = `${user.username} profile picture`;
        
        avatarContainer.appendChild(avatarImg);
        avatarLink.appendChild(avatarContainer);
        visibleContent.appendChild(avatarLink);
    }
    
    if (showName) {
        const textContainer = document.createElement('div');
        textContainer.className = 'flex flex-col min-w-0 flex-1';
        
        const mainLineContainer = document.createElement('div');
        mainLineContainer.className = 'flex items-center gap-2 flex-wrap';
        
        const usernameLink = document.createElement('a');
        usernameLink.className = 'font-medium hover:link truncate';
        usernameLink.textContent = user.displayname || user.username;
        mainLineContainer.appendChild(usernameLink);
        
        if (showCurrentDate || localDate) {
            const dateSpan = document.createElement('span');
            dateSpan.className = 'text-sm text-base-content/60 whitespace-nowrap cursor-default';
            
            if (localDate) {
                const date = new Date(localDate);
                dateSpan.textContent = date.toLocaleDateString();
            } else if (showCurrentDate) {
                dateSpan.textContent = new Date().toLocaleDateString();
            }

            if (showExpanded) {
                dateSpan.addEventListener('click', (e) => {
                    e.stopPropagation();
                });
            }
            
            mainLineContainer.appendChild(dateSpan);
        }
        
        textContainer.appendChild(mainLineContainer);
        
        if (subtext) {
            const subtextSpan = document.createElement('span');
            subtextSpan.className = 'text-sm text-base-content/70 mt-1 leading-tight cursor-default';
            subtextSpan.innerHTML = subtext;
            textContainer.appendChild(subtextSpan);

            if (showExpanded) {
                subtextSpan.addEventListener('click', (e) => {
                    e.stopPropagation();
                });
            }

            visibleContent.classList.add('items-start')
        } else {
            visibleContent.classList.add('items-center')
        }
        
        visibleContent.appendChild(textContainer);
    }
    
    if (visibleSlotHTML) {
        const visibleSlot = document.createElement('span');
        visibleSlot.innerHTML = visibleSlotHTML;
        visibleContent.appendChild(visibleSlot);
    }
    
    container.appendChild(visibleContent);
    
    if (showExpanded) {
        const expandedCard = document.createElement('div');
        
        expandedCard.className = 'fixed hidden bg-base-100 shadow-lg rounded-lg p-4 w-64 z-[9999] border border-base-300';
        expandedCard.id = `user-card-${user.id}-${Math.random().toString(36).substr(2, 9)}`;
        
        const expandedContent = document.createElement('div');
        expandedContent.className = 'text-sm text-base-content';
        
        const headerSection = document.createElement('div');
        headerSection.className = 'flex items-center gap-3 mb-3 pb-3 border-b border-base-300';
        
        const expandedAvatarLink = document.createElement('a');
        expandedAvatarLink.href = `${topDomain}/users/${user.username}`;
        expandedAvatarLink.className = 'avatar hover:opacity-80 transition-opacity';
        
        const expandedAvatarContainer = document.createElement('div');
        expandedAvatarContainer.className = 'size-12 rounded-full';
        
        const expandedAvatarImg = document.createElement('img');
        expandedAvatarImg.src = user.image;
        expandedAvatarImg.className = 'w-full h-full object-cover';
        expandedAvatarImg.alt = user.username;
        
        expandedAvatarContainer.appendChild(expandedAvatarImg);
        expandedAvatarLink.appendChild(expandedAvatarContainer);
        headerSection.appendChild(expandedAvatarLink);
        
        const userInfoContainer = document.createElement('div');
        userInfoContainer.className = 'flex-1 min-w-0';
        
        const displayNameLink = document.createElement('a');
        displayNameLink.href = `${topDomain}/users/${user.username}`;
        displayNameLink.className = 'font-semibold hover:link block truncate';
        displayNameLink.textContent = user.displayname || user.username;
        if (localUser && localUser.id === user.id) {
            displayNameLink.textContent = t('u.you', { username: displayNameLink.textContent });
        }
        userInfoContainer.appendChild(displayNameLink);
        
        const usernameSpan = document.createElement('span');
        usernameSpan.className = 'text-base-content/60 block truncate';
        usernameSpan.textContent = `@${user.username}`;
        userInfoContainer.appendChild(usernameSpan);
        
        headerSection.appendChild(userInfoContainer);
        expandedContent.appendChild(headerSection);
        
        if (user.types && user.types.length > 0) {
            const badgesDiv = document.createElement('div');
            badgesDiv.className = 'flex flex-wrap gap-2 mb-3';
            
            const sortedTypes = [...user.types].sort();
            sortedTypes.forEach((type, index) => {
                const badge = document.createElement('span');
                badge.className = index === 0 ? 'badge badge-accent badge-sm' : 'badge badge-secondary badge-sm';
                badge.textContent = type;
                badgesDiv.appendChild(badge);
            });
            
            expandedContent.appendChild(badgesDiv);
        }
        
        if (localUser && localUser.friends && user.friends && localUser.friends.length > 0 && localUser.id !== user.id) {
            let friendCount = 0;
            let isFriend = false;
            
            localUser.friends.forEach((friend) => {
                if (user.friends.includes(friend)) {
                    friendCount++;
                }
                if (friend === user.id) {
                    isFriend = true;
                }
            });
        
            const friendSection = document.createElement('div');
            if (isFriend || friendCount > 0) {
                friendSection.className = 'mb-3 p-2 bg-base-200 rounded text-xs';
                
                if (isFriend) {
                    const friendP = document.createElement('p');
                    friendP.className = 'text-success font-medium';
                    friendP.innerHTML = `<i class="fas fa-user-friends mr-1"></i>${t('profile.friends')}`;
                    friendSection.appendChild(friendP);
                }
                
                if (friendCount > 0) {
                    const mutualP = document.createElement('p');
                    mutualP.className = isFriend ? 'mt-1' : '';
                    mutualP.innerHTML = friendCount == 1 ? t('profile.mutual', { count: friendCount }) : t('profile.mutuals', { count: friendCount });
                    friendSection.appendChild(mutualP);
                }
                
            }
            if (!isFriend) {
                const notFriends = document.createElement('div');
                notFriends.className = 'flex flex-row gap-2 w-full';
                const friendP = document.createElement('p');
                friendP.className = 'font-medium mb-3 p-2 bg-base-200 rounded text-xs w-full';
                friendP.innerHTML = `<i class="fas fa-user-friends mr-1"></i>${t('profile.notFriends')}`;
                const addFriend = document.createElement('a');

                notFriends.appendChild(friendP);
                notFriends.appendChild(addFriend);

                friendSection.appendChild(notFriends);
            }
            expandedContent.appendChild(friendSection);
        }
        
        if (cfg.extraHoverHTML) {
            const extraDiv = document.createElement('div');
            extraDiv.className = 'mb-3';
            extraDiv.innerHTML = cfg.extraHoverHTML;
            expandedContent.appendChild(extraDiv);
        }
        
        const footerSection = document.createElement('div');
        footerSection.className = 'pt-3 border-t border-base-300 flex justify-between items-center text-xs text-base-content/60';
        
        const createdSpan = document.createElement('span');
        const createdDate = new Date(user.createdAt).toLocaleDateString();
        createdSpan.textContent = `${t('u.createdAt', { date: createdDate })}`;
        footerSection.appendChild(createdSpan);
        
        const profileLink = document.createElement('a');
        profileLink.href = `${topDomain}/users/${user.username}`;
        profileLink.className = 'btn btn-sm btn-primary';
        profileLink.innerHTML = '<i class="fas fa-user mr-1"></i>' + t('u.profile');
        footerSection.appendChild(profileLink);
        
        expandedContent.appendChild(footerSection);
        expandedCard.appendChild(expandedContent);

        document.body.appendChild(expandedCard);
        const positionCard = () => {
            const triggerRect = visibleContent.getBoundingClientRect();
            const cardWidth = 256; 
            const cardHeight = expandedCard.offsetHeight || 400; 
            const margin = 16; 
            
            let left, top;
            switch(pos) {
                case 'right':
                    left = triggerRect.right + margin;
                    top = triggerRect.top;
                    if (left + cardWidth > window.innerWidth) {
                        left = triggerRect.left - cardWidth - margin;
                    }
                    break;
                case 'left':
                    left = triggerRect.left - cardWidth - margin;
                    top = triggerRect.top;
                    if (left < 0) {
                        left = triggerRect.right + margin;
                    }
                    break;
                case 'top':
                    left = triggerRect.left + (triggerRect.width / 2) - (cardWidth / 2);
                    top = triggerRect.top - cardHeight - margin;
                    if (top < 0) {
                        top = triggerRect.bottom + margin;
                    }
                    break;
                case 'bottom':
                    left = triggerRect.left + (triggerRect.width / 2) - (cardWidth / 2);
                    top = triggerRect.bottom + margin;
                    break;
                case 'bottom-left':
                    left = triggerRect.right - cardWidth;
                    top = triggerRect.bottom + margin;
                    break;
                case 'bottom-right':
                default:
                    left = triggerRect.left;
                    top = triggerRect.bottom + margin;
                    break;
            }
            
            left = Math.max(margin, Math.min(left, window.innerWidth - cardWidth - margin));
            top = Math.max(margin, Math.min(top, window.innerHeight - cardHeight - margin));
            
            expandedCard.style.left = `${left}px`;
            expandedCard.style.top = `${top}px`;
        };
        
        let isExpanded = false;
        
        const toggleExpanded = (e) => {
            e.stopPropagation();
            isExpanded = !isExpanded;
            if (isExpanded) {
                document.querySelectorAll('[id^="user-card-"]').forEach(card => {
                    if (card !== expandedCard) {
                        card.classList.add('hidden');
                    }
                });
                positionCard();
                expandedCard.classList.remove('hidden');
            } else {
                expandedCard.classList.add('hidden');
            }
        };
        
        const closeExpanded = (e) => {
            if (!expandedCard.contains(e.target) && !container.contains(e.target)) {
                expandedCard.classList.add('hidden');
                isExpanded = false;
            }
        };
        
        visibleContent.addEventListener('click', toggleExpanded);
        
        document.addEventListener('click', closeExpanded);
        
        expandedCard.addEventListener('click', (e) => {
            e.stopPropagation();
        });
        
        const updatePosition = () => {
            if (isExpanded) {
                positionCard();
            }
        };
        window.addEventListener('scroll', updatePosition, true);
        window.addEventListener('resize', updatePosition);
        
        container._cleanup = () => {
            document.removeEventListener('click', closeExpanded);
            window.removeEventListener('scroll', updatePosition, true);
            window.removeEventListener('resize', updatePosition);
            if (expandedCard.parentNode === document.body) {
                document.body.removeChild(expandedCard);
            }
        };
    }
    
    wrapper.appendChild(container);
    
    return container;
}

function cleanupUserCard(container) {
    if (container._cleanup) {
        container._cleanup();
    }
}
function closeAllUserCards() {
    document.querySelectorAll('[id^="user-card-"]').forEach(card => {
        card.classList.add('hidden');
    });
}